import Dropdown from '@/components/common/Dropdown/Dropdown';
import FormModal from '@/components/common/Modal/FormModal';
import { useErrorContext } from '@/context/ErrorContext';
import { IOptions } from '@/data/models/option';
import { ITrack } from '@/data/models/track';
import useGetAlbumQuery from '@/hooks/useGetAlbumQuery';
import useMoveTracksToVolume from '@/hooks/useMoveTracksVolume';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  show: boolean;
  onClose: () => void;
  usesSides: boolean;
  volumeNumbers: number[];
  onAddVolume: (volumeNumber: number) => void;
  albumId: string;
  selectedTracks: ITrack[];
  allTracks: ITrack[];
  reloadTracks: () => Promise<void>;
}

export default function MoveTracksToVolumeModal({
  show,
  onClose,
  usesSides,
  volumeNumbers,
  onAddVolume,
  albumId,
  selectedTracks,
  allTracks,
  reloadTracks,
}: Props) {
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const { moveTracks, isPending } = useMoveTracksToVolume();

  const [selectedVolume, setSelectedVolume] = useState<IOptions | null>(null);
  const prevAvailableVolumesRef = useRef<number[]>([]);

  const { data: album } = useGetAlbumQuery(albumId);

  const availableVolumes = useMemo(() => {
    const selectedVols = new Set(
      selectedTracks.map((track) => track.discOrSideNumber)
    );

    return selectedVols.size === 1
      ? volumeNumbers.filter((volume) => !selectedVols.has(volume))
      : volumeNumbers;
  }, [selectedTracks, volumeNumbers]);

  const options: IOptions[] = useMemo(() => {
    const opts = availableVolumes.map((num) => ({
      value: num.toString(),
      label: usesSides
        ? `${t('product.side')} ${String.fromCharCode(64 + num)}`
        : `${t('product.disc')} ${num}`,
    }));

    opts.push({
      value: 'new',
      label: (
        <i>
          {usesSides
            ? t('product.editProduct.newSide')
            : t('product.editProduct.newDisc')}
        </i>
      ) as any,
    });

    return opts;
  }, [availableVolumes, usesSides, t]);

  useEffect(() => {
    if (!show || options.length === 0) return;

    if (!selectedVolume) {
      setSelectedVolume(options[0]);
    } else if (
      availableVolumes.length > prevAvailableVolumesRef.current.length
    ) {
      const newVolume = availableVolumes.find(
        (volume) => !prevAvailableVolumesRef.current.includes(volume)
      );

      const newOption = options.find(
        (option) => option.value === newVolume?.toString()
      );

      if (newOption) {
        setSelectedVolume(newOption);
      }
    } else {
      const selectedStillExists = options.some(
        (option) => option.value === selectedVolume.value
      );

      if (!selectedStillExists) {
        setSelectedVolume(options[0]);
      }
    }

    prevAvailableVolumesRef.current = availableVolumes;
  }, [show, availableVolumes, options, selectedVolume]);

  const handleSubmit = () => {
    if (!selectedVolume || !album) return;

    let targetVolume: number;
    let isNewVolume = false;

    if (selectedVolume.value === 'new') {
      targetVolume =
        volumeNumbers.length > 0 ? Math.max(...volumeNumbers) + 1 : 1;

      isNewVolume = true;
    } else {
      targetVolume = Number(selectedVolume.value);
    }

    moveTracks(
      albumId,
      album,
      allTracks,
      selectedTracks,
      targetVolume,
      async () => {
        if (isNewVolume && volumeNumbers.length > 0) {
          onAddVolume(volumeNumbers[volumeNumbers.length - 1]);
        }
        await reloadTracks();
        onClose();
      },
      () => {
        setError(t('common.error.defaultError'));
        onClose();
      }
    );
  };

  const title = usesSides
    ? t('product.moveTracksToVolumeModal.selectSide')
    : t('product.moveTracksToVolumeModal.selectDisc');

  if (!show) return null;

  return (
    <FormModal
      show={show}
      onClose={onClose}
      onSubmit={handleSubmit}
      titleText={title}
      submitButtonText={t('common.button.save')}
      submitButtonDisabled={isPending}
    >
      <label className="modal-field-label upper-case">{title}</label>

      <div>
        <Dropdown
          id="volumeSelector"
          options={options}
          selectedOption={selectedVolume}
          onOptionChange={(value: IOptions | null) => setSelectedVolume(value)}
          isClearable={false}
        />
      </div>
    </FormModal>
  );
}
